const DEFAULT_COPY_OPTIONS = {
  course_name_abbr: true,
  tee_time_interval: true,
  green_depth: true,
  hole_labels: true,
  shotgun_priority: true,
  pace_of_play: true,
  default_tee_by_gender: true,
  default_tee_by_course: true,
  tee_name_abbr: true,
  tee_color: true,
  stroke_index: false,
  par: false,
  yardage: true,
  tee_pace_of_play: true,
  tee_hole_labels: true,
}

export const SOURCE_CONFIGS = {
  crs: {
    title: () => window.I18n.t('courses.bootstrap_courses.import_from_usga'),
    importUrlKey: 'importCrsUrl',
    hasWizard: true,
    courseIdField: 'crs_course_id',
  },
  scorecentre: {
    title: () => window.I18n.t('courses.bootstrap_courses.import_from_score_centre'),
    importUrlKey: 'importScorecentreUrl',
    hasWizard: true,
    courseIdField: 'scorecentre_course_id',
  },
  golf_advisor: {
    title: () => window.I18n.t('courses.bootstrap_courses.import_from_golf_now'),
    importUrlKey: 'importGolfAdvisorUrl',
    hasWizard: false,
    courseIdField: 'course_id',
  },
  ngf: {
    title: (terminology) => window.I18n.t('courses.bootstrap_courses.import_from', { integration_name: terminology }),
    importUrlKey: 'importNgfUrl',
    hasWizard: false,
    courseIdField: 'course_id',
  },
  whs_uk: {
    title: () => window.I18n.t('courses.bootstrap_courses.import_from_whs'),
    importUrlKey: 'importWhsUkUrl',
    hasWizard: false,
    courseIdField: 'course_uid',
  },
  generic_whs: {
    title: (terminology) => window.I18n.t('courses.bootstrap_courses.import_from', { integration_name: terminology }),
    importUrlKey: 'importGenericWhsUrl',
    hasWizard: false,
    courseIdField: 'course_id',
  },
}

export const INITIAL_STATE = {
  step: 1,
  selectedCourseId: null,
  selectedCourseName: null,
  selectedTeeIds: [],
  selectedClubId: null,
  levelSelections: {},
  copyInfo: false,
  existingCourseId: null,
  archiveOldCourse: true,
  copyOptions: DEFAULT_COPY_OPTIONS,
  matchedTees: {},
  existingCourseTees: [],
  additionalTeeIds: [],
  autoMatchApplied: false,
}

export const ACTION_TYPES = {
  RESET: 'RESET',
  NEXT_STEP: 'NEXT_STEP',
  PREV_STEP: 'PREV_STEP',
  SELECT_COURSE: 'SELECT_COURSE',
  SET_SELECTED_TEE_IDS: 'SET_SELECTED_TEE_IDS',
  SELECT_CLUB: 'SELECT_CLUB',
  SELECT_LEVEL: 'SELECT_LEVEL',
  SET_COPY_INFO: 'SET_COPY_INFO',
  SET_EXISTING_COURSE: 'SET_EXISTING_COURSE',
  SET_ARCHIVE_OLD_COURSE: 'SET_ARCHIVE_OLD_COURSE',
  SET_COPY_OPTION: 'SET_COPY_OPTION',
  MATCH_TEE: 'MATCH_TEE',
  APPLY_AUTO_MATCH: 'APPLY_AUTO_MATCH',
  TOGGLE_ADDITIONAL_TEE: 'TOGGLE_ADDITIONAL_TEE',
}

const computeUnmatchedIds = (existingCourseTees, matchedTees) => {
  const matchedExistingIds = new Set(Object.values(matchedTees).map(String).filter((id) => id && id !== '-1'))
  return existingCourseTees.filter((t) => !matchedExistingIds.has(String(t.id))).map((t) => String(t.id))
}

// Preserves user's unchecked choices while adding newly unmatched tees and removing newly matched ones.
const syncAdditionalTees = (existingCourseTees, newMatchedTees, currentAdditionalTeeIds) => {
  const newUnmatchedIds = new Set(computeUnmatchedIds(existingCourseTees, newMatchedTees))
  const kept = currentAdditionalTeeIds.filter((id) => newUnmatchedIds.has(id))
  const added = [...newUnmatchedIds].filter((id) => !currentAdditionalTeeIds.includes(id))
  return [...kept, ...added]
}

export const wizardReducer = (state, action) => {
  switch (action.type) {
    case ACTION_TYPES.RESET:
      return INITIAL_STATE

    case ACTION_TYPES.NEXT_STEP:
      return { ...state, step: state.step + 1 }

    case ACTION_TYPES.PREV_STEP:
      return { ...state, step: state.step - 1 }

    case ACTION_TYPES.SELECT_COURSE:
      return {
        ...state,
        selectedCourseId: action.courseId,
        selectedCourseName: action.courseName,
        selectedTeeIds: [],
        matchedTees: {},
        existingCourseTees: [],
        additionalTeeIds: [],
        autoMatchApplied: false,
      }

    case ACTION_TYPES.SET_SELECTED_TEE_IDS:
      return { ...state, selectedTeeIds: action.teeIds }

    case ACTION_TYPES.SELECT_CLUB:
      return { ...state, selectedClubId: action.clubId }

    case ACTION_TYPES.SELECT_LEVEL: {
      const next = { ...state.levelSelections, [action.level]: action.value }
      action.childLevels.forEach((l) => { delete next[l] })
      return { ...state, levelSelections: next }
    }

    case ACTION_TYPES.SET_COPY_INFO:
      return { ...state, copyInfo: action.value }

    case ACTION_TYPES.SET_EXISTING_COURSE:
      return {
        ...state,
        existingCourseId: action.courseId,
        matchedTees: {},
        existingCourseTees: [],
        additionalTeeIds: [],
        autoMatchApplied: false,
      }

    case ACTION_TYPES.SET_ARCHIVE_OLD_COURSE:
      return { ...state, archiveOldCourse: action.value }

    case ACTION_TYPES.SET_COPY_OPTION:
      return { ...state, copyOptions: { ...state.copyOptions, [action.key]: action.value } }

    case ACTION_TYPES.MATCH_TEE: {
      const newMatchedTees = { ...state.matchedTees, [action.crsId]: action.existingId }
      return {
        ...state,
        matchedTees: newMatchedTees,
        additionalTeeIds: syncAdditionalTees(state.existingCourseTees, newMatchedTees, state.additionalTeeIds),
      }
    }

    case ACTION_TYPES.APPLY_AUTO_MATCH: {
      if (state.autoMatchApplied) return state
      const unmatchedIds = computeUnmatchedIds(action.existingCourseTees, action.matches)
      return {
        ...state,
        matchedTees: action.matches,
        existingCourseTees: action.existingCourseTees,
        additionalTeeIds: unmatchedIds,
        autoMatchApplied: true,
      }
    }

    case ACTION_TYPES.TOGGLE_ADDITIONAL_TEE: {
      const ids = action.checked
        ? state.additionalTeeIds.includes(action.teeId)
          ? state.additionalTeeIds
          : [...state.additionalTeeIds, action.teeId]
        : state.additionalTeeIds.filter((id) => id !== action.teeId)
      return { ...state, additionalTeeIds: ids }
    }

    default:
      return state
  }
}

// Selectors

export const getMatchedElsewhereIds = (matchedTees, excludeCrsId) =>
  new Set(
    Object.entries(matchedTees)
      .filter(([crsId, existingId]) => String(crsId) !== String(excludeCrsId) && existingId && existingId !== '-1')
      .map(([, existingId]) => existingId)
  )

export const getTotalSteps = (source, state) => {
  const { hasWizard } = SOURCE_CONFIGS[source]
  if (!hasWizard) return 1
  return state.copyInfo ? 4 : 2
}

export const getCanGoNext = (source, state, ngfDefaultClubId) => {
  const { step, selectedCourseId, selectedClubId, copyInfo, existingCourseId } = state
  if (step === 1) {
    if (source === 'ngf') return !!selectedCourseId && !!(selectedClubId || ngfDefaultClubId)
    return !!selectedCourseId
  }
  if (step === 2) return !copyInfo || !!existingCourseId
  return true
}

export const getShowImportOnStep2 = (source, state) => {
  const { hasWizard } = SOURCE_CONFIGS[source]
  return hasWizard && state.step === 2 && !state.copyInfo
}

// Builds the POST params for the import request
export const buildImportParams = (source, state) => {
  const config = SOURCE_CONFIGS[source]
  const { courseIdField } = config

  if (source === 'ngf') {
    return {
      urlKey: config.importUrlKey,
      club_id: state.selectedClubId || state.ngfDefaultClubId,
      course_id: state.selectedCourseId,
      course_name: state.selectedCourseName,
    }
  }

  if (source === 'whs_uk') {
    return {
      urlKey: config.importUrlKey,
      course_uid: state.selectedCourseId,
      course_name: state.selectedCourseName,
    }
  }

  if (!config.hasWizard) {
    return {
      urlKey: config.importUrlKey,
      [courseIdField]: state.selectedCourseId,
      course_name: state.selectedCourseName,
    }
  }

  // Wizard sources (crs, scorecentre)
  const params = {
    urlKey: config.importUrlKey,
    [courseIdField]: state.selectedCourseId,
    copy_course_information: state.copyInfo ? '1' : '0',
    'tee_ids_to_import[]': state.selectedTeeIds,
  }

  if (source === 'crs') {
    params.crs_course_name = state.selectedCourseName
  }

  if (state.copyInfo) {
    params.existing_course_id = state.existingCourseId
    params.archive_old_course = state.archiveOldCourse ? '1' : '0'

    Object.entries(state.copyOptions).forEach(([key, val]) => {
      params[key] = val ? '1' : '0'
    })

    Object.entries(state.matchedTees).forEach(([crsId, existingId]) => {
      params[`tee_ids[${crsId}]`] = existingId
    })

    params.additional_tee_ids = state.additionalTeeIds
  }

  return params
}
